import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Target, TrendingUp, CheckCircle2, AlertCircle, BookOpen, Award, ArrowRight, RotateCcw, BarChart2, ChevronRight, LayoutDashboard } from 'lucide-react';
import { AssessmentReport } from './types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { Badge } from '../ui/Badge';
interface AssessmentReportPageProps {
  report: AssessmentReport;
  onExit: () => void;
  onRetake?: () => void;
  onPracticeWeakAreas?: () => void;
}
type Tab = 'overzicht' | 'vakken' | 'aandacht' | 'actie';
export function AssessmentReportPage({
  report,
  onExit,
  onRetake,
  onPracticeWeakAreas
}: AssessmentReportPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overzicht');
  const {
    score,
    estimatedLevels,
    overallEstimatedLevelCode,
    overallExpectedLevelCode,
    growthIndicators,
    focusAreasBySkill,
    strengthsBySkill,
    actionPlan
  } = report;
  // Determine celebration level based on score
  const showConfetti = score.percentage >= 70;
  const confettiMessage = score.percentage >= 90 ? 'WAUW! Je bent een echte meester! 🎓' : score.percentage >= 80 ? 'Super gedaan! 🌟' : score.percentage >= 70 ? 'Heel goed bezig! 💪' : '';
  const tabs = [{
    id: 'overzicht' as Tab,
    label: 'Overzicht',
    icon: LayoutDashboard
  }, {
    id: 'vakken' as Tab,
    label: 'Per Vak',
    icon: BarChart2
  }, {
    id: 'aandacht' as Tab,
    label: 'Sterk & Zwak',
    icon: Target
  }, {
    id: 'actie' as Tab,
    label: 'Actieplan',
    icon: TrendingUp
  }];
  // Helper for Rocket icon since it's not imported above
  function Rocket(props: any) {
    return <TrendingUp {...props} data-id="element-1202" />;
  }
  return <div className="h-screen flex flex-col bg-slate-50 overflow-hidden font-sans" data-id="element-1203">
      <ConfettiCelebration trigger={showConfetti} message={confettiMessage} data-id="element-1204" />

      {/* Header */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-4 shadow-sm z-10" data-id="element-1205">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-1206">
          <div className="flex items-center gap-4" data-id="element-1207">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center" data-id="element-1208">
              <Trophy className="w-6 h-6 text-indigo-600" data-id="element-1209" />
            </div>
            <div data-id="element-1210">
              <h1 className="text-xl font-black text-slate-900 tracking-tight" data-id="element-1211">
                Mijn Leerlingprofiel
              </h1>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1212">
                Groep {report.selectedGroup} ·{' '}
                {new Date(report.completedAt).toLocaleDateString('nl-NL', {
                month: 'long',
                year: 'numeric'
              })}
              </p>
            </div>
          </div>
          <Button onClick={onExit} variant="ghost" size="sm" className="text-slate-600 hover:bg-slate-100" data-id="element-1213">
            Sluiten
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex-none bg-white border-b border-slate-200 px-4" data-id="element-1214">
        <div className="max-w-5xl mx-auto" data-id="element-1215">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-1216">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-4 font-bold text-sm transition-all relative whitespace-nowrap ${isActive ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1217">
                  <Icon className={`w-4 h-4 ${isActive ? 'stroke-[2.5px]' : ''}`} data-id="element-1218" />
                  <span data-id="element-1219">{tab.label}</span>
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full" data-id="element-1220" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto bg-slate-50/50" data-id="element-1221">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-8" data-id="element-1222">
          <AnimatePresence mode="wait" data-id="element-1223">
            {/* OVERZICHT TAB */}
            {activeTab === 'overzicht' && <motion.div key="overzicht" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-6" data-id="element-1224">
                {/* Hero Card */}
                <Card className="p-8 bg-gradient-to-br from-indigo-600 to-violet-700 text-white border-0 shadow-xl overflow-hidden relative" data-id="element-1225">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl" data-id="element-1226"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-900/30 rounded-full -ml-10 -mb-10 blur-2xl" data-id="element-1227"></div>

                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-1228">
                    <div className="text-center md:text-left flex-1" data-id="element-1229">
                      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold mb-4 border border-white/20" data-id="element-1230">
                        <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" data-id="element-1231" />
                        <span data-id="element-1232">Jouw resultaat</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-black mb-2 tracking-tight" data-id="element-1233">
                        Niveau {overallEstimatedLevelCode}
                      </h2>
                      <p className="text-indigo-100 text-lg font-medium mb-6" data-id="element-1234">
                        {overallEstimatedLevelCode === overallExpectedLevelCode ? 'Je zit precies op het verwachte niveau. Goed bezig!' : overallEstimatedLevelCode > overallExpectedLevelCode ? 'Wauw! Je zit boven het verwachte niveau!' : 'Je bent goed op weg. Blijf oefenen!'}
                      </p>

                      <div className="flex flex-wrap gap-4 justify-center md:justify-start" data-id="element-1235">
                        <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm border border-white/10" data-id="element-1236">
                          <div className="text-xs text-indigo-200 uppercase font-bold tracking-wider mb-1" data-id="element-1237">
                            Score
                          </div>
                          <div className="text-2xl font-black" data-id="element-1238">
                            {score.percentage}%
                          </div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm border border-white/10" data-id="element-1239">
                          <div className="text-xs text-indigo-200 uppercase font-bold tracking-wider mb-1" data-id="element-1240">
                            Verwacht
                          </div>
                          <div className="text-2xl font-black" data-id="element-1241">
                            {overallExpectedLevelCode}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Visual Level Indicator */}
                    <div className="w-full md:w-auto flex justify-center" data-id="element-1242">
                      <div className="relative w-48 h-48" data-id="element-1243">
                        <svg className="w-full h-full transform -rotate-90" data-id="element-1244">
                          <circle cx="50%" cy="50%" r="45%" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12" data-id="element-1245" />
                          <motion.circle cx="50%" cy="50%" r="45%" fill="none" stroke="white" strokeWidth="12" strokeLinecap="round" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: score.percentage / 100
                      }} transition={{
                        duration: 1.5,
                        ease: 'easeOut'
                      }} style={{
                        strokeDasharray: '283',
                        strokeDashoffset: '0'
                      }} data-id="element-1246" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-1247">
                          <div className="text-5xl font-black" data-id="element-1248">
                            {score.correctCount}
                          </div>
                          <div className="text-sm font-medium text-indigo-200" data-id="element-1249">
                            van {score.totalQuestions} goed
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Growth Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1250">
                  <Card className="p-6 border-l-4 border-l-emerald-500" data-id="element-1251">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1252">
                      <TrendingUp className="w-5 h-5 text-emerald-500" data-id="element-1253" />
                      Waar je groeit
                    </h3>
                    <div className="space-y-3" data-id="element-1254">
                      {growthIndicators.filter(g => ['groei_snel', 'gaat_goed', 'ik_groei'].includes(g.level)).slice(0, 3).map(g => {
                    const module = estimatedLevels.find(m => m.moduleId === g.moduleId);
                    return <div key={g.moduleId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-1255">
                              <div className="font-medium text-slate-700" data-id="element-1256">
                                {module?.moduleTitle}
                              </div>
                              <div className={`text-sm font-bold ${g.color} flex items-center gap-1`} data-id="element-1257">
                                {g.emoji} {g.label}
                              </div>
                            </div>;
                  })}
                      {growthIndicators.filter(g => ['groei_snel', 'gaat_goed', 'ik_groei'].includes(g.level)).length === 0 && <p className="text-slate-500 italic text-sm" data-id="element-1258">
                          Nog geen sterke groei zichtbaar. Blijf oefenen!
                        </p>}
                    </div>
                  </Card>

                  <Card className="p-6 border-l-4 border-l-amber-500" data-id="element-1259">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1260">
                      <Target className="w-5 h-5 text-amber-500" data-id="element-1261" />
                      Aandachtspunten
                    </h3>
                    <div className="space-y-3" data-id="element-1262">
                      {growthIndicators.filter(g => ['let_op', 'extra_aandacht'].includes(g.level)).slice(0, 3).map(g => {
                    const module = estimatedLevels.find(m => m.moduleId === g.moduleId);
                    return <div key={g.moduleId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-1263">
                              <div className="font-medium text-slate-700" data-id="element-1264">
                                {module?.moduleTitle}
                              </div>
                              <div className={`text-sm font-bold ${g.color} flex items-center gap-1`} data-id="element-1265">
                                {g.emoji} {g.label}
                              </div>
                            </div>;
                  })}
                      {growthIndicators.filter(g => ['let_op', 'extra_aandacht'].includes(g.level)).length === 0 && <p className="text-slate-500 italic text-sm" data-id="element-1266">
                          Geen specifieke aandachtspunten. Super!
                        </p>}
                    </div>
                  </Card>
                </div>
              </motion.div>}

            {/* VAKKEN TAB */}
            {activeTab === 'vakken' && <motion.div key="vakken" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-4" data-id="element-1267">
                <Card className="overflow-hidden" data-id="element-1268">
                  <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center" data-id="element-1269">
                    <h3 className="font-bold text-slate-700" data-id="element-1270">Niveau per vak</h3>
                    <div className="text-sm text-slate-500 flex items-center gap-2" data-id="element-1271">
                      <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-1272"></span>
                      Verwacht niveau:{' '}
                      <strong data-id="element-1273">{overallExpectedLevelCode}</strong>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-1274">
                    {estimatedLevels.map(level => {
                  const growth = growthIndicators.find(g => g.moduleId === level.moduleId);
                  const isUnknown = level.questionCount < 3;
                  return <div key={level.moduleId} className="p-6 hover:bg-slate-50 transition-colors" data-id="element-1275">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-3" data-id="element-1276">
                            <div className="w-32 font-bold text-slate-900" data-id="element-1277">
                              {level.moduleTitle}
                            </div>
                            <div className="flex-1" data-id="element-1278">
                              {/* Niveau Bar */}
                              <div className="h-4 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-1279">
                                {/* Expected Marker */}
                                <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400 z-10 border-r border-dashed border-slate-600" style={{
                            left: `${level.expectedTier / 16 * 100}%`
                          }} data-id="element-1280" />

                                {/* Actual Level */}
                                <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${level.estimatedTier / 16 * 100}%`
                          }} transition={{
                            duration: 1,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${isUnknown ? 'bg-slate-300' : level.delta >= 0 ? 'bg-emerald-500' : level.delta >= -1 ? 'bg-yellow-400' : 'bg-amber-500'}`} data-id="element-1281" />
                              </div>
                            </div>
                            <div className="w-24 text-right font-bold text-slate-700" data-id="element-1282">
                              {isUnknown ? '?' : level.estimatedLevelCode}
                            </div>
                          </div>

                          <div className="flex justify-between items-center text-sm pl-0 sm:pl-36" data-id="element-1283">
                            <div className="flex items-center gap-2" data-id="element-1284">
                              {growth && <span className={`font-medium ${growth.color} flex items-center gap-1`} data-id="element-1285">
                                  {growth.emoji} {growth.label}
                                </span>}
                            </div>
                            <div className="text-slate-400 text-xs" data-id="element-1286">
                              {level.questionCount} vragen
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </Card>
              </motion.div>}

            {/* AANDACHT TAB (Sterk & Zwak) */}
            {activeTab === 'aandacht' && <motion.div key="aandacht" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1287">
                {/* Sterke Punten */}
                <Card className="border-t-4 border-t-emerald-500 h-full" data-id="element-1288">
                  <div className="p-6 border-b border-slate-100 bg-emerald-50/30" data-id="element-1289">
                    <div className="flex items-center gap-3" data-id="element-1290">
                      <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1291">
                        <Star className="w-5 h-5 text-emerald-600 fill-emerald-600" data-id="element-1292" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1293">
                        Sterke Punten
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-6" data-id="element-1294">
                    {strengthsBySkill.length > 0 ? strengthsBySkill.slice(0, 3).map(area => <div key={area.moduleId} data-id="element-1295">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1296">
                            {area.moduleTitle}
                          </div>
                          <ul className="space-y-3" data-id="element-1297">
                            {area.skills.map(skill => <li key={skill.skillName} className="flex items-start gap-3" data-id="element-1298">
                                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1299" />
                                <div data-id="element-1300">
                                  <div className="font-semibold text-slate-800 capitalize" data-id="element-1301">
                                    {skill.skillName}
                                  </div>
                                  <div className="text-sm text-slate-500" data-id="element-1302">
                                    {skill.families.length} onderwerpen ·{' '}
                                    {Math.round(skill.avgAccuracy)}% goed
                                  </div>
                                </div>
                              </li>)}
                          </ul>
                        </div>) : <div className="text-center py-8 text-slate-400 italic" data-id="element-1303">
                        Nog geen specifieke sterke punten gevonden.
                      </div>}
                  </div>
                </Card>

                {/* Aandachtspunten */}
                <Card className="border-t-4 border-t-amber-500 h-full" data-id="element-1304">
                  <div className="p-6 border-b border-slate-100 bg-amber-50/30" data-id="element-1305">
                    <div className="flex items-center gap-3" data-id="element-1306">
                      <div className="bg-amber-100 p-2 rounded-lg" data-id="element-1307">
                        <Target className="w-5 h-5 text-amber-600" data-id="element-1308" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1309">
                        Aandachtspunten
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-6" data-id="element-1310">
                    {focusAreasBySkill.length > 0 ? focusAreasBySkill.slice(0, 3).map(area => <div key={area.moduleId} data-id="element-1311">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1312">
                            {area.moduleTitle}
                          </div>
                          <ul className="space-y-3" data-id="element-1313">
                            {area.skills.map(skill => <li key={skill.skillName} className="flex items-start gap-3" data-id="element-1314">
                                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1315" />
                                <div data-id="element-1316">
                                  <div className="font-semibold text-slate-800 capitalize" data-id="element-1317">
                                    {skill.skillName}
                                  </div>
                                  <div className="text-sm text-slate-500" data-id="element-1318">
                                    {skill.families.length} onderwerpen ·{' '}
                                    {Math.round(skill.avgAccuracy)}% goed
                                  </div>
                                </div>
                              </li>)}
                          </ul>
                        </div>) : <div className="text-center py-8 text-slate-400 italic" data-id="element-1319">
                        Geen specifieke aandachtspunten gevonden.
                      </div>}
                  </div>
                </Card>
              </motion.div>}

            {/* ACTIEPLAN TAB */}
            {activeTab === 'actie' && <motion.div key="actie" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-6" data-id="element-1320">
                {/* 1. Begin Met */}
                {actionPlan.beginMet && <Card className="border-l-4 border-l-indigo-600 overflow-hidden" data-id="element-1321">
                    <div className="p-6" data-id="element-1322">
                      <div className="flex items-center gap-2 mb-4" data-id="element-1323">
                        <Badge variant="primary" size="sm" data-id="element-1324">
                          Stap 1
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1325">
                          Begin hiermee
                        </h3>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1326">
                        <div data-id="element-1327">
                          <div className="text-sm text-slate-500 mb-1" data-id="element-1328">
                            {actionPlan.beginMet.moduleTitle} ›{' '}
                            {actionPlan.beginMet.skillName}
                          </div>
                          <div className="text-xl font-bold text-slate-800 mb-2" data-id="element-1329">
                            {actionPlan.beginMet.familyDescription}
                          </div>
                          <p className="text-slate-600 max-w-xl" data-id="element-1330">
                            Dit onderwerp vond je lastig tijdens de toets. Door
                            hier mee te beginnen maak je de meeste winst!
                          </p>
                        </div>
                        <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 whitespace-nowrap" data-id="element-1331">
                          Start Oefenen <ArrowRight className="w-4 h-4 ml-2" data-id="element-1332" />
                        </Button>
                      </div>
                    </div>
                  </Card>}

                {/* 2. Ga Verder Met */}
                {actionPlan.gaVerderMet.length > 0 && <Card className="border-l-4 border-l-emerald-500 overflow-hidden" data-id="element-1333">
                    <div className="p-6" data-id="element-1334">
                      <div className="flex items-center gap-2 mb-4" data-id="element-1335">
                        <Badge variant="success" size="sm" data-id="element-1336">
                          Stap 2
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1337">
                          Ga verder met
                        </h3>
                      </div>

                      <div className="space-y-4" data-id="element-1338">
                        {actionPlan.gaVerderMet.map((item, idx) => <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-id="element-1339">
                            <div data-id="element-1340">
                              <div className="text-xs text-slate-500 mb-1" data-id="element-1341">
                                {item.moduleTitle}
                              </div>
                              <div className="font-bold text-slate-800" data-id="element-1342">
                                {item.familyDescription}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" data-id="element-1343" />
                          </div>)}
                      </div>
                    </div>
                  </Card>}

                {/* 3. Uitdaging */}
                {actionPlan.uitdaging && <Card className="border-l-4 border-l-yellow-400 overflow-hidden bg-gradient-to-r from-yellow-50 to-white" data-id="element-1344">
                    <div className="p-6" data-id="element-1345">
                      <div className="flex items-center gap-2 mb-4" data-id="element-1346">
                        <Badge variant="warning" size="sm" data-id="element-1347">
                          Uitdaging
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1348">
                          Klaar voor meer?
                        </h3>
                      </div>

                      <div className="flex items-center gap-4" data-id="element-1349">
                        <div className="bg-yellow-100 p-3 rounded-full" data-id="element-1350">
                          <Trophy className="w-6 h-6 text-yellow-600" data-id="element-1351" />
                        </div>
                        <div data-id="element-1352">
                          <div className="font-bold text-slate-800 text-lg" data-id="element-1353">
                            {actionPlan.uitdaging.suggestion}
                          </div>
                          <div className="text-slate-600" data-id="element-1354">
                            Je bent erg goed in{' '}
                            {actionPlan.uitdaging.moduleTitle}. Tijd voor het
                            volgende niveau!
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-none bg-white border-t border-slate-200 p-4 z-10" data-id="element-1355">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-3" data-id="element-1356">
          {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="flex-1 bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200" data-id="element-1357">
              <BookOpen className="w-5 h-5 mr-2" data-id="element-1358" />
              Direct oefenen
            </Button>}
          <Button onClick={onExit} variant="secondary" size="lg" className="flex-1" data-id="element-1359">
            Terug naar Dashboard
          </Button>
        </div>
      </div>
    </div>;
}